import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { updateProfile } from 'firebase/auth';

import {
  changePassword,
  deleteAccount,
  hasPasswordProvider,
  logout,
  resetPassword,
} from '../../firebase/auth';
import { getAuthHeaders } from '../../utils/userUtils';
import {
  buildStartTrialMetaPayload,
  createStartTrialEventId,
  trackMetaStartTrialPixel,
} from '../../utils/paymentTracking';
import {
  trackFreeTrialClick,
  trackFreeTrialStart,
  trackGroup50Event,
  trackPro50Event,
  trackPersonalOfferClick,
} from '../../utils/eventTracker';
import { buildGroup50LandingUrl, isGroup50EventActive } from '../../utils/group50Campaign';
import { PRO50_EVENT_END_LABEL, buildPro50LandingUrl, isPro50Active } from '../../utils/pro50Campaign';
import { validatePassword } from '../../utils/passwordPolicy';
import { useToast } from '../../components/Toast';
import ConfirmDialog, { useConfirm } from '../../components/common/ConfirmDialog';
import Dialog from '../../components/common/Dialog';
import ModalActions from '../../components/common/ModalActions';
import Notice from '../../components/common/Notice';
import { canUseInviteFeature } from '../../api/inviteApi';
import InviteAccountCard from '../../components/invite/InviteAccountCard';

// 쿠폰 시스템 API
import { claimCoupon } from '../../api/couponService';

// 아이콘 및 디자인 시스템
import {
  UserIcon, DiamondIcon, CouponIcon,
  LogOutIcon, TrashIcon, XIcon, CheckCircleIcon
} from '../../components/icons/ChatCategoryIcons.js';
import '../../styles/design-system/index.css';
import './SubscriptionPage.css';

export const CHAT_SETTINGS_PATH = '/chat/settings';
export const LEGACY_PERSONALIZATION_HASHES = ['#ai-memory', '#ai-personalization'];

const INITIAL_PASSWORD_FORM = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

export const getPasswordChangeErrorMessage = (error, stage) => {
  const code = error?.code || '';

  if (
    stage === 'reauthenticate'
    && [
      'auth/invalid-credential',
      'auth/invalid-login-credentials',
      'auth/wrong-password',
    ].includes(code)
  ) {
    return '현재 비밀번호가 올바르지 않아요. 다시 확인해주세요.';
  }

  switch (code) {
    case 'auth/too-many-requests':
      return '보호를 위해 시도가 잠시 제한됐어요. 잠시 후 다시 시도하거나 재설정 메일을 이용해주세요.';
    case 'auth/weak-password':
    case 'auth/password-does-not-meet-requirements':
      return '새 비밀번호가 보안 기준을 충족하지 않아요. 다른 비밀번호를 입력해주세요.';
    case 'auth/network-request-failed':
      return '네트워크 연결을 확인한 뒤 다시 시도해주세요.';
    case 'auth/requires-recent-login':
      return '보안 확인 시간이 지났어요. 현재 비밀번호를 다시 입력해주세요.';
    case 'auth/no-current-user':
    case 'auth/user-token-expired':
    case 'auth/invalid-user-token':
    case 'auth/user-disabled':
    case 'auth/user-mismatch':
      return '로그인 정보를 다시 확인해야 해요. 로그아웃 후 다시 로그인해주세요.';
    case 'auth/password-provider-required':
      return '이메일과 비밀번호로 로그인한 계정에서만 변경할 수 있어요.';
    default:
      return '비밀번호를 변경하지 못했어요. 잠시 후 다시 시도해주세요.';
  }
};

const REFUNDABLE_PAYMENT_STATUSES = new Set(['completed', 'done', 'paid', 'success']);

const getPersonalOfferCtaLabel = (offer, price) => {
  if (offer?.id === 'trial50') return '50% 할인가로 시작하기';
  if (offer?.id === 'renewal20') return '20% 할인가로 연장하기';

  if (offer?.id === 'invite_welcome') {
    const explicitDiscount = Number(price?.discount_amount);
    const calculatedDiscount = (
      Number(price?.original_price) - Number(price?.final_price)
    );
    const discountAmount = explicitDiscount > 0
      ? explicitDiscount
      : calculatedDiscount;

    return discountAmount > 0
      ? `${discountAmount.toLocaleString()}원 할인받기`
      : '첫 결제 할인받기';
  }

  return '할인가로 결제하기';
};

export const getPaymentKey = (payment) => (
  payment?.transaction_id
  || payment?.payment_key
  || payment?.id
  || null
);

export const getLatestRefundablePayment = (payments = []) => (
  payments.find((payment) => (
    REFUNDABLE_PAYMENT_STATUSES.has(String(payment?.status || 'completed').toLowerCase())
    && Number(payment?.amount || 0) > 0
    && Boolean(getPaymentKey(payment))
  )) || null
);

const DOCUMENT_STATUSES = new Set(['completed', 'done', 'paid', 'success', 'partial_canceled']);
const NON_PAYMENT_METHODS = new Set(['coupon', 'referral', 'invite', 'free', 'trial', 'admin']);

export const canGetPaymentDocuments = (payment) => {
  const status = String(payment?.status || '').toLowerCase();
  if (!DOCUMENT_STATUSES.has(status) || payment?.refund_reconciliation_required) return false;
  if (NON_PAYMENT_METHODS.has(String(payment?.payment_method || '').toLowerCase())) return false;
  const amount = status === 'partial_canceled'
    ? Number(payment?.retained_amount || 0)
    : Number(payment?.amount || 0);
  return amount > 0 && Boolean(getPaymentKey(payment));
};

const normalizeDisplayName = (value) => {
  const normalized = (value || '').trim();

  if (normalized.length < 2 || normalized.length > 30) {
    return { error: '계정명은 2자 이상 30자 이하로 입력해주세요.' };
  }

  const hasControlCharacter = Array.from(normalized).some((char) => {
    const code = char.charCodeAt(0);
    return code < 32 || code === 127;
  });

  if (hasControlCharacter) {
    return { error: '계정명에는 줄바꿈을 사용할 수 없어요.' };
  }

  if (/[<>]/.test(normalized)) {
    return { error: '계정명에는 꺾쇠괄호를 사용할 수 없어요.' };
  }

  if (/https?:\/\/|www\./i.test(normalized)) {
    return { error: '계정명에는 URL을 사용할 수 없어요.' };
  }

  return { value: normalized };
};

const SubscriptionPage = () => {
  const { currentUser, loading: authLoading, refreshCurrentUserProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();
  const confirmDialog = useConfirm();

  // AI 맞춤 설정은 /chat/settings 허브로 이동했다. 예전 앵커 링크(토스트·북마크·안내 글)는
  // 그대로 새 위치로 보낸다 (replace: 뒤로 가기 때 요금제 페이지로 튀지 않게).
  useEffect(() => {
    const legacyHash = window.location.hash;
    if (LEGACY_PERSONALIZATION_HASHES.includes(legacyHash)) {
      navigate(`${CHAT_SETTINGS_PATH}${legacyHash}`, { replace: true });
    }
  }, [navigate]);
  const { 
    subscriptionStatus,
    fetchSubscriptionStatus,
    loading
  } = useSubscription();
  
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundLoading, setRefundLoading] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const paymentRequestRef = useRef(0);
  // 7일 환불 윈도우 초과 시 표시할 안내 모달
  const [showRefundExpiredModal, setShowRefundExpiredModal] = useState(false);
  const [refundExpiredInfo, setRefundExpiredInfo] = useState(null); // { daysSincePaid, lastPaidAt }

  // 환불 정책: 결제 후 7일 이내만 자동 환불 (전자상거래법 제17조 + 약관)
  const REFUND_WINDOW_DAYS = 7;
  
  // 프로필 관련 상태
  const [error, setError] = useState('');
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletionStage, setDeletionStage] = useState('');
  const [deletionEligible, setDeletionEligible] = useState(null);
  const [deletionReason, setDeletionReason] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [displayNameInput, setDisplayNameInput] = useState('');
  const [displayNameError, setDisplayNameError] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState(INITIAL_PASSWORD_FORM);
  const [passwordError, setPasswordError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);
  const passwordTriggerRef = useRef(null);
  const currentPasswordInputRef = useRef(null);

  // 쿠폰 관련 상태
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');

  // 7일 무료 체험 상태
  const [trialLoading, setTrialLoading] = useState(false);
  const [trialError, setTrialError] = useState('');
  // null = 확인 중, true/false = 확인 완료. 서버가 구독·결제 이력·활성 계정 해시 표식을 종합해 판정한다.
  const [trialUsed, setTrialUsed] = useState(null);
  const trialRequestRef = useRef(0);

  // 서버가 자격과 가격을 확정한 개인 할인 오퍼
  const [availableOffer, setAvailableOffer] = useState(null);
  const [offerLoading, setOfferLoading] = useState(false);
  const offerRequestRef = useRef(0);

  // API 기본 URL 설정
  const getApiBaseUrl = () => {
    const currentUrl = window.location.hostname;
    if (currentUrl.includes('localhost')) {
      return 'http://localhost:8000';
    } else if (currentUrl.includes('whatquiztest')) {
      return 'https://quiz-api-test-397277209875.us-central1.run.app';
    } else {
      return 'https://quiz-api-397277209875.us-central1.run.app';
    }
  };

  const beginEditDisplayName = () => {
    setDisplayNameInput(currentUser?.displayName || '');
    setDisplayNameError('');
    setIsEditingName(true);
  };

  const cancelEditDisplayName = () => {
    setDisplayNameInput('');
    setDisplayNameError('');
    setIsEditingName(false);
  };

  const canChangePassword = Boolean(
    currentUser
    && typeof currentUser.getIdToken === 'function'
    && hasPasswordProvider(currentUser)
  );

  const clearPasswordForm = () => {
    setPasswordForm(INITIAL_PASSWORD_FORM);
    setPasswordError('');
  };

  const openPasswordModal = () => {
    clearPasswordForm();
    setShowPasswordModal(true);
  };

  const closePasswordModal = () => {
    if (passwordLoading || passwordResetLoading) return;
    setShowPasswordModal(false);
    clearPasswordForm();
    passwordTriggerRef.current?.focus();
  };

  const handlePasswordFieldChange = (field, value) => {
    setPasswordForm((previous) => ({
      ...previous,
      [field]: value,
    }));
    setPasswordError('');
  };

  // ESC·포커스 트랩·스크림 클릭·스크롤 잠금은 Dialog 프리미티브가 맡는다.
  const handlePasswordChange = async (event) => {
    event.preventDefault();
    const {
      currentPassword,
      newPassword,
      confirmPassword,
    } = passwordForm;

    if (!canChangePassword) {
      setPasswordError('이 계정에서는 비밀번호를 변경할 수 없어요.');
      return;
    }
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('현재 비밀번호와 새 비밀번호를 모두 입력해주세요.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('새 비밀번호가 서로 일치하지 않아요.');
      return;
    }

    const policyError = validatePassword(newPassword);
    if (policyError) {
      setPasswordError(policyError);
      return;
    }
    if (currentPassword === newPassword) {
      setPasswordError('현재 비밀번호와 다른 새 비밀번호를 입력해주세요.');
      return;
    }

    try {
      setPasswordLoading(true);
      setPasswordError('');
      const result = await changePassword(currentPassword, newPassword);

      if (!result?.success) {
        setPasswordError(
          getPasswordChangeErrorMessage(result?.error, result?.stage)
        );
        setPasswordForm((previous) => ({
          ...previous,
          currentPassword: '',
        }));
        currentPasswordInputRef.current?.focus();
        return;
      }

      setShowPasswordModal(false);
      clearPasswordForm();
      showToast('비밀번호를 변경했어요', 'success');
      passwordTriggerRef.current?.focus();
    } catch (unexpectedError) {
      console.error(
        '비밀번호 변경 처리 실패:',
        unexpectedError?.code || 'auth/unknown'
      );
      setPasswordError(
        getPasswordChangeErrorMessage(unexpectedError, 'update')
      );
      setPasswordForm((previous) => ({
        ...previous,
        currentPassword: '',
      }));
      currentPasswordInputRef.current?.focus();
    } finally {
      setPasswordLoading(false);
    }
  };

  const handlePasswordResetEmail = async () => {
    if (!currentUser?.email) {
      setPasswordError('가입 이메일을 확인할 수 없어요. 다시 로그인해주세요.');
      return;
    }

    try {
      setPasswordResetLoading(true);
      setPasswordError('');
      const result = await resetPassword(currentUser.email);

      if (!result?.success) {
        if (
          result?.error?.status === 429
          || result?.error?.code === 'auth/too-many-requests'
        ) {
          setPasswordError('재설정 메일을 여러 번 요청했어요. 잠시 후 다시 시도해주세요.');
        } else if (result?.error?.code === 'auth/network-request-failed') {
          setPasswordError('네트워크 연결을 확인한 뒤 다시 시도해주세요.');
        } else {
          setPasswordError('재설정 메일을 보내지 못했어요. 잠시 후 다시 시도해주세요.');
        }
        return;
      }

      setShowPasswordModal(false);
      clearPasswordForm();
      showToast(
        '가입 이메일로 비밀번호 재설정 링크를 보냈어요',
        'success',
        6000
      );
      passwordTriggerRef.current?.focus();
    } catch (unexpectedError) {
      console.error(
        '비밀번호 재설정 메일 처리 실패:',
        unexpectedError?.code || 'auth/unknown'
      );
      setPasswordError('재설정 메일을 보내지 못했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setPasswordResetLoading(false);
    }
  };

  const handleSaveDisplayName = async () => {
    const normalized = normalizeDisplayName(displayNameInput);
    if (normalized.error) {
      setDisplayNameError(normalized.error);
      return;
    }

    if (!currentUser || typeof currentUser.getIdToken !== 'function') {
      setDisplayNameError('로그인 정보를 확인할 수 없어요. 다시 로그인해주세요.');
      return;
    }

    if (normalized.value === (currentUser.displayName || '').trim()) {
      cancelEditDisplayName();
      return;
    }

    try {
      setProfileSaving(true);
      setDisplayNameError('');
      setError('');

      await updateProfile(currentUser, { displayName: normalized.value });
      await currentUser.getIdToken(true);
      const headers = await getAuthHeaders(currentUser);
      const API_BASE_URL = getApiBaseUrl();

      const response = await fetch(`${API_BASE_URL}/api/user/profile`, {
        method: 'PATCH',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ display_name: normalized.value }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) {
        throw new Error(result.detail || result.error || '계정명을 저장하지 못했어요.');
      }

      await refreshCurrentUserProfile?.();
      setIsEditingName(false);
      setDisplayNameInput('');
      showToast('계정명을 저장했어요', 'success');
    } catch (err) {
      console.error('계정명 저장 실패:', err);
      const message = err.message || '계정명을 저장하지 못했어요. 잠시 후 다시 시도해주세요.';
      setDisplayNameError(message);
      showToast(message, 'error');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      setError('');
      setLogoutLoading(true);
      const result = await logout();
      if (result.error) {
        setError('로그아웃하지 못했어요. 잠시 후 다시 시도해주세요.');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError('로그아웃하지 못했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setLogoutLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deletionEligible === null) {
      try {
        setDeleteLoading(true);
        const API_BASE_URL = getApiBaseUrl();
        const headers = await getAuthHeaders(currentUser);
        
        const response = await fetch(`${API_BASE_URL}/api/user/deletion-eligibility`, { headers });
        
        if (response.ok) {
          const result = await response.json();
          setDeletionEligible(result.eligible);
          setDeletionReason(result.reason || '');
          if (!result.eligible) {
            setDeleteLoading(false);
            return;
          }
        } else if (response.status === 401 || response.status === 403) {
          setError('로그인이 만료됐어요. 다시 로그인해주세요.');
          setDeleteLoading(false);
          return;
        } else {
          setError('탈퇴 가능 여부를 확인하지 못했어요. 잠시 후 다시 시도해주세요.');
          setDeleteLoading(false);
          return;
        }
      } catch (error) {
        setError('탈퇴 가능 여부를 확인하지 못했어요. 네트워크 연결을 확인해주세요.');
        setDeleteLoading(false);
        return;
      }
    }
    
    if (deletionEligible === false) {
      setError(deletionReason || '지금은 탈퇴할 수 없어요. 잠시 후 다시 시도해주세요.');
      setDeleteLoading(false);
      return;
    }
    
    const isConfirmed = await confirmDialog({
      title: '정말 탈퇴할까요?',
      description: (
        <ul>
          <li>입력·생성·업로드한 데이터를 모두 지우고 되돌릴 수 없어요</li>
          <li>탈퇴 후 30일 동안은 다시 가입할 수 없어요</li>
          <li>결제 이력이 있다면 법정 최소 기록만 따로 보관해요</li>
        </ul>
      ),
      confirmLabel: '탈퇴하기',
      danger: true,
    });
    if (!isConfirmed) return;
    
    try {
      setError('');
      setDeleteLoading(true);
      setDeletionStage('preparing');
      setTimeout(() => setDeletionStage('data'), 500);
      
      const result = await deleteAccount();
      
      if (result.error) {
        setError('탈퇴를 처리하지 못했어요. 잠시 후 다시 시도해주세요.');
        setDeletionStage('');
      } else {
        setDeletionStage('complete');
        setTimeout(() => {
          navigate('/');
          showToast('탈퇴를 완료했어요. 30일 후 다시 가입할 수 있어요', 'success', 6000);
        }, 1000);
      }
    } catch (err) {
      setError('탈퇴를 처리하지 못했어요. 잠시 후 다시 시도해주세요.');
      setDeletionStage('');
    } finally {
      setDeleteLoading(false);
    }
  };

  // 결제 내역 조회
  const fetchPaymentHistory = useCallback(async () => {
    if (!currentUser) return;
    const requestId = ++paymentRequestRef.current;
    const requestedUid = currentUser.uid;
    setPaymentLoading(true);
    try {
      const API_BASE_URL = getApiBaseUrl();
      const headers = await getAuthHeaders(currentUser);
      const response = await fetch(`${API_BASE_URL}/api/payment/history?limit=10`, { headers });
      if (response.ok) {
        const result = await response.json();
        if (
          result.success
          && paymentRequestRef.current === requestId
          && currentUser.uid === requestedUid
        ) {
          setPaymentHistory(result.data.payments || []);
        }
      }
    } catch (error) {
      if (paymentRequestRef.current === requestId) {
        console.error('결제 내역 조회 실패:', error);
      }
    } finally {
      if (paymentRequestRef.current === requestId) {
        setPaymentLoading(false);
      }
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      setPaymentHistory([]);
      fetchPaymentHistory();
    }
    return () => {
      paymentRequestRef.current += 1;
    };
  }, [currentUser, fetchPaymentHistory]);

  // 무료 체험 사용 여부 조회
  const fetchTrialStatus = useCallback(async () => {
    if (!currentUser) return;
    const requestId = ++trialRequestRef.current;
    const requestedUid = currentUser.uid;
    try {
      const API_BASE_URL = getApiBaseUrl();
      const headers = await getAuthHeaders(currentUser);
      const response = await fetch(`${API_BASE_URL}/api/payment/subscription/trial-status`, { headers });
      if (!response.ok) throw new Error(`trial-status ${response.status}`);
      const result = await response.json();
      if (
        trialRequestRef.current === requestId
        && currentUser.uid === requestedUid
      ) {
        setTrialUsed(Boolean(result?.trial_used));
      }
    } catch (error) {
      // 확인에 실패하면 안내를 숨긴다. 시작할 수 없는 사용자에게 버튼을 보여주는 쪽이 더 나쁜 경험이다.
      if (trialRequestRef.current === requestId) {
        console.error('체험 상태 확인 실패:', error);
        setTrialUsed(true);
      }
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      setTrialUsed(null);
      fetchTrialStatus();
    }
    return () => {
      trialRequestRef.current += 1;
    };
  }, [currentUser, fetchTrialStatus]);

  useEffect(() => {
    const requestId = ++offerRequestRef.current;
    const requestedUid = currentUser?.uid;
    setAvailableOffer(null);

    if (!currentUser) {
      setOfferLoading(false);
      return undefined;
    }

    setOfferLoading(true);
    const fetchAvailableOffer = async () => {
      try {
        const API_BASE_URL = getApiBaseUrl();
        const headers = await getAuthHeaders(currentUser);
        const response = await fetch(`${API_BASE_URL}/api/payment/my-offers`, { headers });
        if (!response.ok) throw new Error(`my-offers ${response.status}`);
        const result = await response.json();
        if (
          offerRequestRef.current === requestId
          && currentUser?.uid === requestedUid
        ) {
          setAvailableOffer(result?.primary_offer || null);
        }
      } catch (offerError) {
        if (offerRequestRef.current === requestId) {
          console.error('개인 할인 혜택 조회 실패:', offerError);
          setAvailableOffer(null);
        }
      } finally {
        if (offerRequestRef.current === requestId) {
          setOfferLoading(false);
        }
      }
    };

    fetchAvailableOffer();
    return () => {
      if (offerRequestRef.current === requestId) {
        offerRequestRef.current += 1;
      }
    };
  }, [currentUser]);

  const latestRefundablePayment = getLatestRefundablePayment(paymentHistory);
  const latestRefundablePaymentKey = getPaymentKey(latestRefundablePayment);

  // 가장 최근 미환불 결제의 timestamp(ISO) → 경과일 계산.
  const getDaysSinceLastPayment = useCallback(() => {
    if (!latestRefundablePayment) return null;
    const lastPaidAtIso = (
      latestRefundablePayment.timestamp
      || latestRefundablePayment.created_at
    );
    if (!lastPaidAtIso) return null;
    try {
      const paidDt = new Date(lastPaidAtIso);
      if (Number.isNaN(paidDt.getTime())) return null;
      const diffMs = Date.now() - paidDt.getTime();
      return Math.floor(diffMs / (1000 * 60 * 60 * 24));
    } catch (_e) {
      return null;
    }
  }, [latestRefundablePayment]);

  // 환불 요청 버튼 클릭 → 7일 정책 사전 분기
  const handleRefundButtonClick = () => {
    const days = getDaysSinceLastPayment();
    if (days !== null && days > REFUND_WINDOW_DAYS) {
      // 7일 초과: 안내 모달로 곧장 전환
      setRefundExpiredInfo({
        daysSincePaid: days,
        lastPaidAtIso: (
          latestRefundablePayment?.timestamp
          || latestRefundablePayment?.created_at
          || null
        ),
      });
      setShowRefundExpiredModal(true);
      return;
    }
    setShowRefundModal(true);
  };

  // 환불 처리 (실제 API 호출)
  const handleRefund = async () => {
    setRefundLoading(true);
    try {
      if (!latestRefundablePaymentKey) {
        showToast(
          '환불할 실제 결제 내역을 찾지 못했어요. 쿠폰이나 추천 혜택은 환불 대상이 아니에요.',
          'error',
          6000
        );
        setRefundLoading(false);
        return;
      }

      const API_BASE_URL = getApiBaseUrl();
      const headers = await getAuthHeaders(currentUser);
      
      const response = await fetch(`${API_BASE_URL}/api/payment/refund`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_key: latestRefundablePaymentKey,
          cancel_reason: '고객 요청에 의한 환불'
        })
      });

      const result = await response.json().catch(() => ({}));
      if (response.ok) {
        showToast(
          result.message || '환불했어요. 환불 금액은 원 결제수단으로 지급돼요.',
          'success',
          6000
        );
        setShowRefundModal(false);
        await fetchSubscriptionStatus(true);
        await fetchPaymentHistory();
      } else {
        // 백엔드 7일 윈도우 거부 응답 처리 (이중 안전망 — 클라이언트에서 사전 분기되지만
        // 결제 직후 paymentHistory 가 갱신되기 전 등 race 케이스 대비)
        const detail = result?.detail;
        if (detail && typeof detail === 'object' && detail.code === 'REFUND_WINDOW_EXPIRED') {
          setShowRefundModal(false);
          setRefundExpiredInfo({
            daysSincePaid: detail.days_since_paid,
            lastPaidAtIso: (
              latestRefundablePayment?.timestamp
              || latestRefundablePayment?.created_at
              || null
            ),
          });
          setShowRefundExpiredModal(true);
          return;
        }
        const detailMsg = (typeof detail === 'string' ? detail : detail?.message) || '알 수 없는 오류';
        showToast(`환불하지 못했어요. ${detailMsg}`, 'error', 6000);
      }
    } catch (error) {
      showToast('환불 처리 중 문제가 생겼어요. 잠시 후 다시 시도해주세요.', 'error', 6000);
    } finally {
      setRefundLoading(false);
    }
  };

  // 7일 초과 안내 모달의 "이메일로 문의하기" — 주문 정보 자동 채움
  const buildRefundInquiryMailto = () => {
    const userEmail = currentUser?.email || '';
    const lastPayment = latestRefundablePayment || {};
    const lastPaidIso = refundExpiredInfo?.lastPaidAtIso || lastPayment?.timestamp || '';
    const lastPaidLabel = lastPaidIso ? new Date(lastPaidIso).toLocaleDateString('ko-KR') : '(확인 필요)';
    const orderNo = lastPayment?.order_id || lastPayment?.transaction_id || lastPayment?.id || '(확인 필요)';
    const planLabel = lastPayment?.description || subscriptionStatus?.type || '왓퀴즈AI Pro';
    const amount = lastPayment?.amount ? `${Number(lastPayment.amount).toLocaleString()}원` : '(확인 필요)';

    const subject = encodeURIComponent('[왓퀴즈AI] 환불 문의 (7일 경과 결제)');
    const body = encodeURIComponent(
      `안녕하세요. 환불 가능 기간(${REFUND_WINDOW_DAYS}일)이 지난 결제 건에 대한 환불을 요청드립니다.\n\n` +
      `- 가입 이메일: ${userEmail}\n` +
      `- 결제일: ${lastPaidLabel}\n` +
      `- 주문/거래번호: ${orderNo}\n` +
      `- 플랜: ${planLabel}\n` +
      `- 결제 금액: ${amount}\n\n` +
      `환불 요청 사유:\n(여기에 환불 사유를 적어주세요)\n\n` +
      `감사합니다.`
    );
    return `mailto:angel@plactical.com?subject=${subject}&body=${body}`;
  };

  // 날짜 포맷팅
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // 금액 포맷팅
  const formatAmount = (amount) => amount?.toLocaleString() || '0';

  // 결제 상태 표시
  const getPaymentStatusDisplay = (status, payment = {}) => {
    if (payment.refund_reconciliation_required) {
      return {
        text: '부분 취소 확인 중',
        className: 'subpage-status-pending',
      };
    }
    switch (status) {
      case 'success':
      case 'completed':
        return { text: '완료', className: 'subpage-status-success' };
      case 'refund_pending':
        return { text: '환불중', className: 'subpage-status-pending' };
      case 'refunded':
        return { text: '환불됨', className: 'subpage-status-refunded' };
      case 'partial_canceled':
        return { text: '일할 환불됨', className: 'subpage-status-refunded' };
      default:
        return { text: '알 수 없음', className: 'subpage-status-unknown' };
    }
  };

  // 쿠폰 등록 처리
  const handleClaimCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('쿠폰 코드를 입력해주세요');
      return;
    }
    setCouponLoading(true);
    setCouponError('');
    try {
      const result = await claimCoupon(couponCode.trim(), currentUser);
      setCouponCode('');
      await fetchSubscriptionStatus(true);
      const benefitDays = result.benefit_days || 30;
      // 백엔드가 멱등성 보장을 위해 이미 등록된 쿠폰 재요청에도 200 OK를 돌려주므로,
      // already_claimed 플래그로 분기해야 "이미 등록된 쿠폰을 또 등록 성공"이라고
      // 잘못 안내되는 UX 버그를 막을 수 있다.
      if (result.already_claimed) {
        showToast('이미 등록한 쿠폰이에요. PRO 혜택이 적용 중이니 그대로 이용하세요', 'info', 5000);
      } else {
        showToast(`${benefitDays}일 PRO 혜택을 등록했어요`, 'success');
      }
    } catch (error) {
      const msg = error.message || '';
      const isNetwork = msg.includes('네트워크') || msg.toLowerCase().includes('failed to fetch') || msg.toLowerCase().includes('network');
      if (isNetwork) {
        setCouponError('네트워크 연결을 확인해주세요. 잠시 후 다시 시도해주세요.');
      } else {
        setCouponError(msg || '쿠폰을 등록하지 못했어요. 코드를 다시 확인해주세요.');
      }
    } finally {
      setCouponLoading(false);
    }
  };

  // 7일 무료 체험 시작 (마이페이지 안내 카드 / 만료 쿠폰 안내 두 곳에서 호출)
  const handleStartFreeTrial = async (triggerFeature = 'subscription_page') => {
    setTrialLoading(true);
    setTrialError('');
    trackFreeTrialClick(triggerFeature);
    try {
      const idToken = await currentUser.getIdToken(true);
      const eventId = createStartTrialEventId();
      const response = await fetch(`${getApiBaseUrl()}/api/payment/subscription/free-trial`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(buildStartTrialMetaPayload({
          eventId,
          triggerFeature,
          triggerPage: '/subscription',
        }))
      });
      const result = await response.json();

      if (result.success) {
        trackFreeTrialStart(triggerFeature);
        trackMetaStartTrialPixel({
          contentName: triggerFeature,
          eventId: result.meta_event_id || eventId,
        });
        showToast('7일 무료 체험이 시작됐어요. 지금 바로 PRO 기능을 써보세요.', 'success', 5000);
        setTimeout(() => window.location.reload(), 1500);
      } else {
        // 서버가 거절한 이유(체험 이력·결제 이력 등)를 그대로 보여준다.
        setTrialError(result.detail || result.error || '체험을 시작하지 못했어요. 잠시 후 다시 시도해주세요.');
      }
    } catch (err) {
      setTrialError('네트워크 연결을 확인해주세요. 잠시 후 다시 시도해주세요.');
    } finally {
      setTrialLoading(false);
    }
  };

  // 토스 영수증 열기
  const openTossReceipt = async (paymentKey) => {
    try {
      const API_BASE_URL = getApiBaseUrl();
      const idToken = await currentUser.getIdToken(true);
      const response = await fetch(`${API_BASE_URL}/api/payment/status/${paymentKey}`, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      if (response.ok) {
        const result = await response.json();
        const receiptUrl = result.data?.receipt?.url;
        if (receiptUrl) {
          window.open(receiptUrl, '_blank', 'noopener,noreferrer');
        } else {
          showToast('영수증을 찾지 못했어요. 결제 내역을 다시 확인해주세요', 'info');
        }
      } else {
        showToast('영수증을 불러오지 못했어요. 잠시 후 다시 시도해주세요', 'error');
      }
    } catch (error) {
      showToast('영수증을 불러오지 못했어요. 네트워크 연결을 확인해주세요', 'error');
    }
  };

  // 환불 영수증 열기
  const openRefundReceipt = async (paymentKey) => {
    try {
      const API_BASE_URL = getApiBaseUrl();
      const idToken = await currentUser.getIdToken(true);
      const response = await fetch(`${API_BASE_URL}/api/payment/status/${paymentKey}`, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      if (response.ok) {
        const result = await response.json();
        const paymentData = result.data;
        if (paymentData.status === 'CANCELED' || paymentData.cancels?.length > 0) {
          const receiptUrl = paymentData.receipt?.url;
          if (receiptUrl) {
            window.open(receiptUrl, '_blank', 'noopener,noreferrer');
          } else {
            showToast('환불 영수증을 찾지 못했어요. 결제 내역을 다시 확인해주세요', 'info');
          }
        } else {
          showToast('아직 환불되지 않은 결제예요', 'info');
        }
      } else {
        showToast('환불 영수증을 불러오지 못했어요. 잠시 후 다시 시도해주세요', 'error');
      }
    } catch (error) {
      showToast('환불 영수증을 불러오지 못했어요. 네트워크 연결을 확인해주세요', 'error');
    }
  };

  // 인증 상태가 결정되기 전(Firebase 초기화 중)에는 시머 UI로 깜빡임 방지
  if (authLoading) {
    const shimmer = {
      background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
      backgroundSize: '400px 100%',
      animation: 'subpage-shimmer 1.5s infinite',
      borderRadius: '8px',
    };
    return (
      <div className="subpage-container">
        <style>{`@keyframes subpage-shimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }`}</style>
        <div className="subpage-content">
          <div className="subpage-header">
            <div style={{ ...shimmer, width: '120px', height: '28px' }} />
          </div>
          <div style={{ ...shimmer, width: '100%', height: '140px', marginBottom: '16px', borderRadius: '16px' }} />
          <div style={{ ...shimmer, width: '100%', height: '80px', marginBottom: '16px', borderRadius: '16px' }} />
        </div>
      </div>
    );
  }

  // 🔐 비로그인 사용자: 흰 화면 대신 로그인 안내 화면 표시 (returnUrl 보존)
  if (!currentUser) {
    const queryString = searchParams.toString();
    const currentPath = '/subscription' + (queryString ? `?${queryString}` : '');
    const redirectUrl = encodeURIComponent(currentPath);
    return (
      <div className="subpage-container">
        <div className="subpage-content">
          <div className="subpage-login-required">
            <div className="subpage-login-required-icon">
              <UserIcon size={32} color="var(--whatquiz-primary)" />
            </div>
            <h2 className="subpage-login-required-title">로그인이 필요해요</h2>
            <p className="subpage-login-required-desc">
              마이페이지를 보려면 먼저 로그인해주세요.
            </p>
            <button
              className="subpage-btn subpage-btn-primary subpage-login-required-btn"
              onClick={() => navigate(`/auth?redirect=${redirectUrl}`)}
            >
              로그인하기
            </button>
            <button
              className="subpage-btn subpage-btn-ghost subpage-login-required-secondary"
              onClick={() => navigate('/')}
            >
              홈으로 돌아가기
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    const shimmer = {
      background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
      backgroundSize: '400px 100%',
      animation: 'subpage-shimmer 1.5s infinite',
      borderRadius: '8px',
    };
    return (
      <div className="subpage-container">
        <style>{`@keyframes subpage-shimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }`}</style>
        <div className="subpage-content">
          <div className="subpage-header">
            <div style={{ ...shimmer, width: '120px', height: '28px' }} />
          </div>
          <div style={{ ...shimmer, width: '100%', height: '140px', marginBottom: '16px', borderRadius: '16px' }} />
          <div style={{ ...shimmer, width: '100%', height: '80px', marginBottom: '16px', borderRadius: '16px' }} />
          <div style={{ ...shimmer, width: '60%', height: '16px', marginBottom: '12px' }} />
          <div style={{ ...shimmer, width: '100%', height: '100px', borderRadius: '16px' }} />
        </div>
      </div>
    );
  }

  const isPro = subscriptionStatus?.isPro || false;
  const isActive = subscriptionStatus?.status === 'active';
  const isExpired = subscriptionStatus?.status === 'expired';
  
  const paymentMethod = subscriptionStatus?.payment_method;
  const isCouponUser = paymentMethod === 'coupon';
  const isReferralUser = paymentMethod === 'referral';
  
  const hasPaymentHistory = Array.isArray(paymentHistory) && paymentHistory.length > 0;
  const isPaidSubscription = Boolean(latestRefundablePayment);

  // 체험 안내는 서버가 "아직 안 썼다"고 확인해준 FREE 사용자에게만 보여준다.
  const canStartTrial = trialUsed === false && !isPro;
  // 단체 50% 이벤트 교차 노출 — 결제를 고민하는 FREE·만료 사용자에게만, 텍스트 링크 수준으로.
  const showPro50Crosslink = (!isPro || isExpired) && isPro50Active();
  const showGroup50Crosslink = (!isPro || isExpired) && !showPro50Crosslink && isGroup50EventActive();
  const offerPlanKey = availableOffer?.recommended_plan || '3month';
  const offerPrice = availableOffer?.prices?.[offerPlanKey] || null;
  const offerDeadlineLabel = availableOffer?.days_left === 0
    ? '오늘까지'
    : `${availableOffer?.days_left || 0}일 남음`;
  const offerCtaLabel = getPersonalOfferCtaLabel(availableOffer, offerPrice);

  return (
    <div className="subpage-container">
      <div className="subpage-content">
        {/* 헤더 */}
        <div className="subpage-header">
          <h1 className="subpage-title">마이페이지</h1>
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div className="subpage-alert-error">
            <XIcon size={18} color="currentColor" />
            <span>{error}</span>
          </div>
        )}

        {/* 삭제 진행 상태 */}
        {deleteLoading && (
          <div className="subpage-alert-info">
            <div className="subpage-spinner-small"></div>
            <span>
              {deletionStage === 'preparing' && '회원 탈퇴 준비 중...'}
              {deletionStage === 'data' && '계정 데이터 삭제 중...'}
              {deletionStage === 'complete' && '삭제 완료! 홈으로 이동합니다.'}
            </span>
          </div>
        )}

        {/* 이용권 상태 카드 */}
        <div className="subpage-card subpage-card-main">
          <div className="subpage-card-header-row">
            <div className="subpage-plan-info">
              <div className="subpage-plan-badge">
                {isPro && isActive ? (
                  <DiamondIcon size={20} color="var(--whatquiz-primary)" />
                ) : null}
                <span className={`subpage-plan-name ${isPro && isActive ? 'pro' : ''}`}>
                  {isPro ? 'PRO' : 'FREE'}
                </span>
              </div>
              {isExpired && (
                <span className="subpage-plan-expired">만료됨</span>
              )}
            </div>
          </div>

          {/* 이용권 기간 정보 */}
          {isPro && isActive && (
            <div className="subpage-dates">
              <div className="subpage-date-item">
                <span className="subpage-date-label">시작일</span>
                <span className="subpage-date-value">{formatDate(subscriptionStatus?.startDate)}</span>
              </div>
              <div className="subpage-date-item">
                <span className="subpage-date-label">만료일</span>
                <span className="subpage-date-value">{formatDate(subscriptionStatus?.endDate)}</span>
              </div>
            </div>
          )}

          {/* 자동 결제 안내 - 간결하게 */}
          <p className="subpage-notice">
            기간제 결제라서 자동으로 청구되지 않아요
          </p>

          {/* 이용권 출처 안내 */}
          {isPro && isActive && (isCouponUser || isReferralUser) && (
            <p className="subpage-source-notice">
              {isCouponUser ? '쿠폰으로 활성화됨' : '추천으로 활성화됨'}
            </p>
          )}

          {/* 서버가 자격과 최종 가격을 확정한 개인 할인 혜택 */}
          {offerLoading && (
            <div className="subpage-offer-skeleton" role="status">
              <span>받을 수 있는 혜택을 확인하고 있어요</span>
              <div />
            </div>
          )}
          {availableOffer && offerPrice && (
            <div className="subpage-offer-card" aria-live="polite">
              <div className="subpage-offer-header">
                <div>
                  <p className="subpage-offer-eyebrow">받을 수 있는 혜택</p>
                  <h2 className="subpage-offer-title">{availableOffer.title}</h2>
                </div>
                <span className="subpage-offer-deadline">{offerDeadlineLabel}</span>
              </div>
              <p className="subpage-offer-desc">{availableOffer.description}</p>
              <div className="subpage-offer-price">
                <span className="subpage-offer-original">
                  {offerPrice.original_price.toLocaleString()}원
                </span>
                <strong>{offerPrice.final_price.toLocaleString()}원</strong>
                <span>{offerPlanKey.replace('month', '개월')}</span>
              </div>
              <button
                className="subpage-offer-btn"
                onClick={() => {
                  trackPersonalOfferClick(availableOffer.id, availableOffer.days_left);
                  navigate(
                    `/subscription/pro?plan=${offerPlanKey}`
                    + `&promo=${availableOffer.promo}&source=account_offer`
                  );
                }}
              >
                {offerCtaLabel}
              </button>
            </div>
          )}

          {/* 7일 무료 체험 안내 (아직 체험을 쓰지 않은 FREE 사용자) */}
          {canStartTrial && (
            <div className="subpage-trial-cta">
              <p className="subpage-trial-cta-desc">
                카드 등록 없이 <strong>7일 동안</strong> PRO 기능을 모두 써볼 수 있어요.
              </p>
              {trialError && (
                <p className="subpage-trial-cta-error">{trialError}</p>
              )}
              <button
                className="subpage-trial-btn"
                onClick={() => handleStartFreeTrial('subscription_page')}
                disabled={trialLoading}
              >
                {trialLoading ? (
                  <span className="subpage-trial-btn-text">시작 중...</span>
                ) : (
                  <>
                    <span className="subpage-trial-btn-text">7일 무료 체험 시작하기</span>
                    <span className="subpage-trial-btn-sub">카드 등록 없이 바로 시작</span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* 액션 버튼 */}
          <div className="subpage-action-row">
            {(!isPro || isExpired) && (
              <button 
                className={`subpage-btn ${(canStartTrial || availableOffer || offerLoading) ? 'subpage-btn-outline' : 'subpage-btn-primary'}`}
                onClick={() => navigate('/pricing')}
              >
                PRO 업그레이드
              </button>
            )}
            {isPro && isActive && isPaidSubscription && (
              <button 
                className="subpage-btn subpage-btn-outline"
                onClick={handleRefundButtonClick}
              >
                환불 요청
              </button>
            )}
          </div>

          {showPro50Crosslink && (
            <Link
              className="subpage-group50-link"
              data-testid="subpage-pro50-link"
              to={buildPro50LandingUrl('account_page')}
              onClick={() => trackPro50Event('crosslink_clicked', { stage: 'account_page' })}
            >
              <span className="subpage-group50-link-muted">{PRO50_EVENT_END_LABEL} PRO 1년 반값 · </span>
              <span className="subpage-group50-link-accent">
                10월 이벤트 보기
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </span>
            </Link>
          )}
          {/* 단체 50% 이벤트 — Primary CTA(체험/업그레이드)와 경쟁하지 않는 한 줄 텍스트 링크 */}
          {showGroup50Crosslink && (
            <Link
              className="subpage-group50-link"
              data-testid="subpage-group50-link"
              to={buildGroup50LandingUrl('account_page')}
              onClick={() => trackGroup50Event('crosslink_clicked', { stage: 'account_page' })}
            >
              <span className="subpage-group50-link-muted">10명 이상이면 1인당 반값 · </span>
              <span className="subpage-group50-link-accent">
                단체 이벤트 보기
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </span>
            </Link>
          )}
        </div>

        {/* 결제 내역 */}
        {hasPaymentHistory && (
          <div className="subpage-card">
            <h2 className="subpage-section-title">결제 내역</h2>
            {paymentLoading ? (
              <div className="subpage-loading-inline">
                <div className="subpage-spinner-small"></div>
              </div>
            ) : (
              <div className="subpage-payment-list">
                {paymentHistory.map((payment, index) => (
                  <div key={index} className="subpage-payment-item">
                    <div className="subpage-payment-left">
                      <span className="subpage-payment-desc">
                        {payment.description || 'PRO 이용권'}
                      </span>
                      <span className="subpage-payment-date">
                        {formatDate(payment.timestamp)}
                      </span>
                    </div>
                    <div className="subpage-payment-right">
                      <span className="subpage-payment-amount">
                        {formatAmount(payment.amount)}원
                      </span>
                      <span className={`subpage-payment-status ${getPaymentStatusDisplay(payment.status, payment).className}`}>
                        {getPaymentStatusDisplay(payment.status, payment).text}
                      </span>
                      {(payment.status === 'success' || payment.status === 'completed') && (
                        <button
                          className="subpage-btn-link"
                          onClick={() => openTossReceipt(payment.transaction_id)}
                        >
                          영수증
                        </button>
                      )}
                      {canGetPaymentDocuments(payment) && (
                        <Link
                          className="subpage-btn-link subpage-btn-link--docs"
                          to={`/subscription/documents/${encodeURIComponent(getPaymentKey(payment))}`}
                        >
                          증빙 서류
                        </Link>
                      )}
                      {['refunded', 'partial_canceled'].includes(payment.status)
                        && !payment.refund_reconciliation_required && (
                        <button
                          className="subpage-btn-link"
                          onClick={() => openRefundReceipt(payment.transaction_id)}
                        >
                          환불영수증
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {canUseInviteFeature(currentUser) && (
          <InviteAccountCard onOpen={() => navigate('/invite')} />
        )}

        {/* 쿠폰 등록 */}
        <div className="subpage-card">
          <h2 className="subpage-section-title">
            <CouponIcon size={18} color="var(--whatquiz-gray-600)" />
            쿠폰 등록
          </h2>
          <div className="subpage-coupon-row">
            <input
              type="text"
              value={couponCode}
              onChange={(e) => {
                setCouponCode(e.target.value.toUpperCase());
                setCouponError('');
              }}
              placeholder="쿠폰 코드 입력"
              className="subpage-input"
              disabled={couponLoading}
            />
            <button
              onClick={handleClaimCoupon}
              disabled={couponLoading || !couponCode.trim()}
              className="subpage-btn subpage-btn-primary"
            >
              {couponLoading ? '등록 중...' : '등록'}
            </button>
          </div>
          {couponError && (
            couponError.includes('만료') ? (
              <div className="subpage-coupon-expired-notice">
                <p className="subpage-coupon-expired-title">{couponError}</p>
                <p className="subpage-coupon-expired-desc">
                  {canStartTrial ? (
                    <>
                      쿠폰은 종료됐지만, 카드 등록 없이 <strong>7일 동안</strong> PRO 기능을 모두 써볼 수 있어요.
                    </>
                  ) : (
                    <>
                      {availableOffer ? (
                        <>위의 <strong>받을 수 있는 혜택</strong>에서 할인 가격을 확인해주세요.</>
                      ) : (
                        <>이 쿠폰은 종료됐지만, 현재 진행 중인 <strong>시즌 할인가</strong>를 확인할 수 있어요.</>
                      )}
                    </>
                  )}
                </p>
                {canStartTrial && trialError && (
                  <p className="subpage-coupon-expired-error">{trialError}</p>
                )}
                <div className="subpage-coupon-expired-actions">
                  {canStartTrial ? (
                    <button
                      className="subpage-trial-btn"
                      onClick={() => handleStartFreeTrial('expired_coupon')}
                      disabled={trialLoading}
                    >
                      {trialLoading ? (
                        <span className="subpage-trial-btn-text">시작 중...</span>
                      ) : (
                        <>
                          <span className="subpage-trial-btn-text">7일 무료 체험 시작하기</span>
                          <span className="subpage-trial-btn-sub">카드 등록 없이 바로 시작</span>
                        </>
                      )}
                    </button>
                  ) : !availableOffer ? (
                    <button
                      className="subpage-btn subpage-btn-primary"
                      onClick={() => navigate('/pricing')}
                    >
                      할인가 보기
                    </button>
                  ) : null}
                  <button
                    className="subpage-btn subpage-btn-outline"
                    onClick={() => navigate('/help/notice/ai-model-upgrade-2026-01')}
                  >
                    자세한 안내 보기
                  </button>
                </div>
              </div>
            ) : (
              <p className="subpage-error-text">{couponError}</p>
            )
          )}
        </div>

        {/* 계정 관리 */}
        <div className="subpage-card">
          <h2 className="subpage-section-title">
            <UserIcon size={18} color="var(--whatquiz-gray-600)" />
            계정
          </h2>
          
          <div className="subpage-profile-row">
            {currentUser.photoURL ? (
              <img src={currentUser.photoURL} alt="" className="subpage-avatar" />
            ) : (
              <div className="subpage-avatar-placeholder">
                {currentUser.displayName?.charAt(0).toUpperCase() || 'U'}
              </div>
            )}
            <div className="subpage-profile-info">
              {isEditingName ? (
                <div className="subpage-name-edit">
                  <label className="subpage-name-edit-label" htmlFor="account-display-name">
                    계정명
                  </label>
                  <input
                    id="account-display-name"
                    type="text"
                    value={displayNameInput}
                    onChange={(e) => {
                      setDisplayNameInput(e.target.value);
                      setDisplayNameError('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSaveDisplayName();
                      }
                      if (e.key === 'Escape') {
                        cancelEditDisplayName();
                      }
                    }}
                    className="subpage-input subpage-name-input"
                    placeholder="계정명 입력"
                    maxLength={30}
                    disabled={profileSaving}
                    autoFocus
                  />
                  <p className={`subpage-name-help ${displayNameError ? 'error' : ''}`}>
                    {displayNameError || '2~30자로 입력해주세요. 다음 결제부터 새 이름이 반영돼요.'}
                  </p>
                </div>
              ) : (
                <span className="subpage-profile-name">
                  {currentUser.displayName || '이름 없음'}
                </span>
              )}
              <span className="subpage-profile-email">{currentUser.email}</span>
            </div>
            {currentUser.emailVerified && (
              <span className="subpage-verified-badge">
                <CheckCircleIcon size={14} color="currentColor" />
                인증됨
              </span>
            )}
          </div>

          {isEditingName && (
            <div className="subpage-name-edit-actions">
              <button
                type="button"
                className="subpage-btn subpage-btn-primary"
                onClick={handleSaveDisplayName}
                disabled={profileSaving}
              >
                {profileSaving ? '저장 중...' : '저장'}
              </button>
              <button
                type="button"
                className="subpage-btn subpage-btn-outline"
                onClick={cancelEditDisplayName}
                disabled={profileSaving}
              >
                취소
              </button>
            </div>
          )}

          <div className="subpage-account-actions">
            {!isEditingName && typeof currentUser.getIdToken === 'function' && (
              <button
                type="button"
                onClick={beginEditDisplayName}
                className="subpage-btn subpage-btn-outline"
              >
                계정명 수정
              </button>
            )}
            {!isEditingName && canChangePassword && (
              <button
                ref={passwordTriggerRef}
                type="button"
                onClick={openPasswordModal}
                className="subpage-btn subpage-btn-outline"
              >
                비밀번호 변경
              </button>
            )}
            <button
              onClick={handleLogout}
              disabled={logoutLoading}
              className="subpage-btn subpage-btn-ghost"
            >
              <LogOutIcon size={16} color="currentColor" />
              {logoutLoading ? '로그아웃 중...' : '로그아웃'}
            </button>
            <button
              onClick={handleDeleteAccount}
              disabled={deleteLoading}
              className="subpage-btn subpage-btn-danger-ghost"
            >
              <TrashIcon size={16} color="currentColor" />
              {deleteLoading ? '처리 중...' : '회원 탈퇴'}
            </button>
          </div>
        </div>
      </div>

      {/* 이메일 계정 비밀번호 변경 모달 — Dialog 프리미티브 위의 폼. 처리 중에는 닫히지 않는다 */}
      <Dialog
        closeLabel="비밀번호 변경 닫기"
        description={'보안을 위해 현재 비밀번호를 한 번 더 확인해요.\n변경하면 다른 기기에서는 다시 로그인해야 할 수 있어요.'}
        dismissible={!(passwordLoading || passwordResetLoading)}
        initialFocus={currentPasswordInputRef}
        onClose={closePasswordModal}
        open={showPasswordModal}
        size="sm"
        testId="subpage-password-dialog"
        title="비밀번호 변경"
      >
        <form
          aria-busy={(passwordLoading || passwordResetLoading) || undefined}
          className="subpage-password-form-wrap"
          onSubmit={handlePasswordChange}
        >
          <div className="subpage-password-form">
            <div className="subpage-password-field">
              <label
                className="subpage-password-label"
                htmlFor="current-password"
              >
                현재 비밀번호
              </label>
              <input
                ref={currentPasswordInputRef}
                aria-describedby={passwordError ? 'password-change-error' : undefined}
                aria-invalid={Boolean(passwordError)}
                autoCapitalize="none"
                autoComplete="current-password"
                className="subpage-input subpage-password-input"
                disabled={passwordLoading || passwordResetLoading}
                id="current-password"
                name="current-password"
                onChange={(event) => handlePasswordFieldChange(
                  'currentPassword',
                  event.target.value
                )}
                spellCheck="false"
                type="password"
                value={passwordForm.currentPassword}
              />
            </div>

            <div className="subpage-password-field">
              <label
                className="subpage-password-label"
                htmlFor="new-password"
              >
                새 비밀번호
              </label>
              <input
                aria-describedby="new-password-help"
                autoCapitalize="none"
                autoComplete="new-password"
                className="subpage-input subpage-password-input"
                disabled={passwordLoading || passwordResetLoading}
                id="new-password"
                maxLength={128}
                name="new-password"
                onChange={(event) => handlePasswordFieldChange(
                  'newPassword',
                  event.target.value
                )}
                spellCheck="false"
                type="password"
                value={passwordForm.newPassword}
              />
            </div>

            <div className="subpage-password-field">
              <label
                className="subpage-password-label"
                htmlFor="confirm-password"
              >
                새 비밀번호 확인
              </label>
              <input
                aria-describedby={passwordError ? 'password-change-error' : undefined}
                autoCapitalize="none"
                autoComplete="new-password"
                className="subpage-input subpage-password-input"
                disabled={passwordLoading || passwordResetLoading}
                id="confirm-password"
                maxLength={128}
                name="confirm-password"
                onChange={(event) => handlePasswordFieldChange(
                  'confirmPassword',
                  event.target.value
                )}
                spellCheck="false"
                type="password"
                value={passwordForm.confirmPassword}
              />
            </div>
          </div>

          <p
            className="subpage-password-help"
            id="new-password-help"
          >
            8자 이상, 영문·숫자·특수문자를 모두 포함해주세요.
          </p>

          {passwordError && (
            <Notice
              className="subpage-password-error"
              compact
              id="password-change-error"
              testId="password-change-error"
              variant="error"
            >
              {passwordError}
            </Notice>
          )}

          {/* 현재 비밀번호를 모르는 경우의 우회 경로 — 텍스트 버튼 (채워진 버튼은 아래 "비밀번호 바꾸기" 하나) */}
          <button
            className="subpage-password-reset-btn"
            disabled={passwordLoading || passwordResetLoading}
            onClick={handlePasswordResetEmail}
            type="button"
          >
            {passwordResetLoading
              ? '재설정 메일 보내는 중'
              : '비밀번호 재설정 메일 받기'}
          </button>

          <ModalActions
            className="subpage-password-actions"
            primary={{
              label: '비밀번호 바꾸기',
              type: 'submit',
              loading: passwordLoading,
              disabled: passwordResetLoading,
            }}
            secondary={{
              label: '취소',
              onClick: closePasswordModal,
              disabled: passwordResetLoading,
            }}
            testId="subpage-password-actions"
          />
        </form>
      </Dialog>

      {/* 환불 확인 (7일 이내) — 파괴적 확인 대화상자. 처리 중에는 스피너와 함께 닫히지 않는다 */}
      <ConfirmDialog
        cancelLabel="취소"
        confirmLabel="환불 요청하기"
        danger
        description={(
          <div className="subpage-refund-summary">
            <p className="subpage-refund-summary__policy">결제 후 7일 이내라 100% 환불할 수 있어요</p>
            {latestRefundablePayment && (
              <p className="subpage-refund-summary__target">
                환불 대상: <strong>{latestRefundablePayment.description || 'PRO 이용권'}</strong>
                {' · '}
                <strong>{formatAmount(latestRefundablePayment.amount)}원</strong>
              </p>
            )}
            <ul className="subpage-refund-summary__list">
              <li>가장 최근 결제 1건을 100% 환불해요</li>
              <li>환불한 결제로 늘어난 이용 기간만 줄어들고, 다른 결제나 쿠폰으로 받은 기간은 그대로예요</li>
              <li>영업일 기준 3~5일 안에 결제수단으로 돌려드려요</li>
              <li>다른 이용 기간이 남아 있지 않으면 PRO 혜택이 종료돼요</li>
            </ul>
          </div>
        )}
        loading={refundLoading}
        onCancel={() => setShowRefundModal(false)}
        onConfirm={handleRefund}
        open={showRefundModal}
        title="환불을 요청할까요?"
      />

      {/* 환불 가능 기간 종료 안내 (7일 초과) — 이메일 문의로 이어 준다 */}
      <Dialog
        closeLabel="환불 안내 닫기"
        description={(
          `결제 후 ${Number.isFinite(refundExpiredInfo?.daysSincePaid) ? `${refundExpiredInfo.daysSincePaid}일` : '7일 이상'}이 지나 마이페이지에서 자동으로 환불하기는 어려워요.\n`
          + '특별한 사정이 있으면 이메일로 알려주세요. 상황을 검토해서 안내드릴게요.'
        )}
        footer={(
          <ModalActions
            primary={{
              label: '이메일로 문의하기',
              onClick: () => { window.location.href = buildRefundInquiryMailto(); },
            }}
            secondary={{ label: '닫기', onClick: () => setShowRefundExpiredModal(false) }}
            testId="subpage-refund-expired-actions"
          />
        )}
        onClose={() => setShowRefundExpiredModal(false)}
        open={showRefundExpiredModal}
        size="sm"
        testId="subpage-refund-expired-dialog"
        title="환불 가능 기간이 지났어요"
      >
        <Notice testId="subpage-refund-contact" variant="neutral">
          <p>
            문의: <a className="subpage-refund-contact__link" href="mailto:angel@plactical.com">angel@plactical.com</a>
          </p>
          <p>
            <a
              className="subpage-refund-contact__link subpage-refund-contact__link--muted"
              href="/refund-policy"
              rel="noopener noreferrer"
              target="_blank"
            >
              환불 정책 전문 보기
            </a>
          </p>
        </Notice>
      </Dialog>
    </div>
  );
};

export default SubscriptionPage;
